import * as React from "react";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { getDefaultOrganizationRoute } from "@simplyk/common";

import { useCurrentUserContext } from "../../contexts/CurrentUserContext";
import { useLocaleContext } from "../../contexts/LocaleContext";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useTranslate } from "../../hooks/useTranslate";
import { LandingPageUrl } from "../../routes/routes";
import Image from "../Image/Image";
import { ZeffyLogoRedirect } from "../ZeffyLogoRedirect/ZeffyLogoRedirect";

import { NotFoundPageProps } from "./types";

import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

const NotFoundPage: React.FC<NotFoundPageProps> = ({ deletedForm }) => {
  const { locale } = useLocaleContext();
  const { currentUser, userPermissions } = useCurrentUserContext();
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const redirectionLink = React.useMemo(() => {
    if (currentUser) {
      return getDefaultOrganizationRoute(userPermissions);
    } else {
      return LandingPageUrl[locale];
    }
  }, [currentUser, locale, userPermissions]);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <ZeffyLogoRedirect src="/images/Zeffy-Logo-Color.svg" disabled />
      <Container
        maxWidth="sm"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Grid container alignItems="center" sx={{ textAlign: "center" }}>
          <Grid item xs={12}>
            <Image
              src="/images/light-bulb.svg"
              alt="Light bulb"
              width={isSmallScreen ? 275 : 550}
              height={isSmallScreen ? 110 : 220}
            />
          </Grid>
          <Grid item xs={12}>
            <DashboardTypography
              variant="h3"
              sx={(theme) => ({ color: theme.palette.warning.main, marginBottom: theme.spacing(2) })}
            >
              {t("common", "notFound.Oops")}
            </DashboardTypography>
          </Grid>
          {deletedForm ? (
            <>
              <Grid item xs={12}>
                <DashboardTypography variant="h5" vibe="primary-main">
                  {t("common", "gone.deletedForm")}
                </DashboardTypography>
              </Grid>
              <Grid item xs={12}>
                <DashboardTypography variant="body1">{t("common", "gone.contactNonProfit")}</DashboardTypography>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <DashboardTypography variant="h5" vibe="primary-main">
                  {t("common", "notFound.doYouHaveTheRightLink")}
                </DashboardTypography>
              </Grid>
              <Grid item xs={12}>
                <DashboardTypography variant="body1">
                  {t("common", "notFound.writeUsTo1")}
                  <DashboardLink
                    href="mailto:contact@zeffy.com"
                    sx={(theme) => ({ marginLeft: theme.spacing(0.5), marginRight: theme.spacing(0.5) })}
                    underline="hover"
                  >
                    contact@zeffy.com
                  </DashboardLink>
                  {t("common", "notFound.writeUsTo2")}
                </DashboardTypography>
              </Grid>
              <Grid item xs={12} sx={(theme) => ({ marginTop: theme.spacing(2) })}>
                <DashboardButton variant={currentUser ? "filled" : "outlined"} vibe="brand" href={redirectionLink}>
                  {t("common", currentUser ? "notFound.returnToDashboard" : "notFound.returnToLanding")}
                </DashboardButton>
              </Grid>
            </>
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default NotFoundPage;
